// src/context/ProjectContext.tsx

/**
 * Project context for the Quantik Mind enterprise frontend.
 *
 * This module provides:
 * - the list of projects available to the authenticated user
 * - the currently selected project
 * - project loading and error state
 * - persistence of the selected project across browser refreshes
 * - a refresh function for reloading project data from the backend
 *
 * The context acts as the shared source of truth for project-scoped frontend
 * operations.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { api, ApiError, AuthError } from '../utils/apiClient';
import { useAuth } from './authContext';
import { formatApiError } from '../utils/formatApiError';

/**
 * Project metadata exposed to the frontend application.
 */
type Project = {
  /** Unique identifier of the project. */
  id: string;

  /** Human-readable project name. */
  name: string;

  /** Optional Prometheus endpoint associated with the project. */
  prometheus_url?: string;

  /** Optional project creation timestamp. */
  created_at?: string;
};

/**
 * Shape of the project context exposed to consumers.
 */
type ProjectContextType = {
  /** Currently selected project, if any. */
  currentProject: Project | null;

  /**
   * Update the currently selected project.
   *
   * Passing null clears the current project selection.
   */
  setCurrentProject: (project: Project | null) => void;

  /** Full list of projects available to the current user. */
  availableProjects: Project[];

  /**
   * Replace the current list of available projects.
   */
  setAvailableProjects: (projects: Project[]) => void;

  /** Indicates whether the project list is currently loading. */
  loading: boolean;

  /** Human-readable error message for project loading failures. */
  error: string | null;

  /**
   * Reload the project list from the backend.
   */
  refreshProjects: () => Promise<void>;
};

/**
 * Shared React context used to expose project state application-wide.
 */
const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

/**
 * Provide project state to the React component tree.
 *
 * The provider restores the selected project from local storage and loads the
 * list of available projects for the authenticated user.
 *
 * @param children - Descendant components that require access to project state.
 * @returns The project context provider.
 */
export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const { authData, authReady } = useAuth();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load the projects available to the authenticated user.
   *
   * If no token is available, the project list is cleared and the request is
   * skipped.
   */
  const loadProjects = async () => {
    if (!authData) {
      setAvailableProjects([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      /**
       * Use the shared API client so authentication failures are handled
       * consistently across the frontend.
       */
      const projects = await api.get<Project[]>('/projects/');

      setAvailableProjects(projects);

      /**
       * Ensure the currently selected project still exists in the refreshed list.
       * If not, clear the stale selection from state and local storage.
       */
      if (currentProject) {
        const stillValid = projects.find(
          (project) => project.id === currentProject.id
        );

        if (!stillValid) {
          setCurrentProject(null);
          localStorage.removeItem('currentProject');
        }
      }
    } catch (err: any) {
      console.error('[PROJECT] Failed to load projects:', err);

      /**
       * Authentication failures are already handled centrally by the API client.
       * No additional redirect logic is needed here.
       */
      if (err instanceof AuthError) {
        return;
      }

      if (err instanceof ApiError) {
        setError(formatApiError(err, 'Failed to load projects'));
      } else {
        setError(formatApiError(err, 'Failed to load projects'));
      }

      setAvailableProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    /**
     * Restore the last selected project from local storage on application
     * startup. Invalid persisted data is discarded.
     */
    const storedProject = localStorage.getItem('currentProject');

    if (storedProject) {
      try {
        const parsed = JSON.parse(storedProject);
        setCurrentProject(parsed);
      } catch {
        localStorage.removeItem('currentProject');
      }
    }
  }, []);

  useEffect(() => {
    /**
     * Load projects when the provider is mounted and whenever token-dependent
     * state changes trigger a re-evaluation of the provider.
     */
    if (!authReady) {
      return;
    }
    void loadProjects();
  }, [authReady, authData?.userId]);

  useEffect(() => {
    /**
     * Persist the current project selection so it can be restored after a page
     * refresh.
     */
    if (currentProject) {
      localStorage.setItem('currentProject', JSON.stringify(currentProject));
    } else {
      localStorage.removeItem('currentProject');
    }
  }, [currentProject]);

  return (
    <ProjectContext.Provider
      value={{
        currentProject,
        setCurrentProject,
        availableProjects,
        setAvailableProjects,
        loading,
        error,
        refreshProjects: loadProjects,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

/**
 * Access the shared project context.
 *
 * @returns The current project context value.
 * @throws Error when used outside of the ProjectProvider tree.
 */
export const useProject = () => {
  const context = useContext(ProjectContext);

  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }

  return context;
};
